import React from 'react';
import * as LucideIcons from 'lucide-react';
import { Layers3, Loader2 } from 'lucide-react';

import SidebarNavItem from '../../../app/shell/Sidebar/SidebarNavItem';
import { getSidebarNavIconColorClass } from '../../../app/shell/Sidebar/sidebarNavStyles';
import { useSandboxSession } from '../../sandbox/SandboxSessionContext';
import { useBranchChangeMarkers } from '../../sandbox/hooks/useBranchChangeMarkers';
import { useOntologyViewsIndex } from '../hooks/useOntologyViews';
import { getLucideIconByName } from '../renderers/shared/utils/lucideIconFromName';
import {
  getViewNavigationLabel,
  getViewSidebarColor,
  getViewSidebarIconName,
  isViewPublished,
  sortViewsForNavigation,
} from '../utils/viewNavigation';

const liveQueries = { useSandboxSession, useBranchChangeMarkers, useOntologyViewsIndex };

/** Published apps the person can open. Drafts stay in App Editor. */
const ViewsNavigationSection = ({
  showLabels = false,
  activePath = '',
  onNavigate,
  buildViewPath,
  queries = liveQueries,
}) => {
  const { sandboxContext, isPreviewActive } = queries.useSandboxSession();
  const { hasViewChanges } = queries.useBranchChangeMarkers();
  const { data: views = [], isLoading } = queries.useOntologyViewsIndex({
    sandboxContext: isPreviewActive ? sandboxContext : null,
  });
  const navigableViews = sortViewsForNavigation((views || []).filter((view) => view?.id && isViewPublished(view)));
  const resolveViewPath = (view) =>
    (typeof buildViewPath === 'function' ? buildViewPath(view) : null) ||
    view?.path ||
    `/app/views/${view.id}`;

  if (!isLoading && navigableViews.length === 0) {
    return null;
  }

  return (
    <div className="space-y-0.5">
      {isLoading ? (
        <div
          className={`flex items-center rounded-lg py-2.5 text-slate-500 ${
            showLabels ? 'px-2.5' : 'justify-center group-hover/sidebar:justify-start group-hover/sidebar:px-2.5'
          }`}
        >
          <Loader2 className="h-[18px] w-[18px] shrink-0 animate-spin text-slate-400" />
          <span
            className={`overflow-hidden whitespace-nowrap text-[13px] font-medium tracking-[-0.01em] transition-[max-width,opacity,margin] duration-200 ease-out ${
              showLabels
                ? 'ml-2.5 max-w-[11rem] opacity-100'
                : 'max-w-0 opacity-0 group-hover/sidebar:ml-2.5 group-hover/sidebar:max-w-[11rem] group-hover/sidebar:opacity-100'
            }`}
          >
            Loading apps
          </span>
        </div>
      ) : (
        navigableViews.map((view, index) => {
          const path = resolveViewPath(view);
          const isActive = activePath.startsWith(path);
          const activeColor = getViewSidebarColor(view, index);
          const iconName = getViewSidebarIconName(view);
          const IconComponent = iconName ? getLucideIconByName(LucideIcons, iconName) : null;
          const Icon = IconComponent || Layers3;

          return (
            <SidebarNavItem
              key={view.id}
              icon={
                <span className="relative">
                  <Icon
                    className={getSidebarNavIconColorClass(activeColor, isActive)}
                    strokeWidth={isActive ? 2.25 : 1.75}
                    aria-hidden
                  />
                </span>
              }
              label={getViewNavigationLabel(view)}
              hasChanges={hasViewChanges(view)}
              isActive={isActive}
              showLabels={showLabels}
              activeColor={activeColor}
              onClick={() => onNavigate?.(path)}
            />
          );
        })
      )}
    </div>
  );
};

export default ViewsNavigationSection;
